import React, { PureComponent } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  BarChart,
  Bar,
  CartesianGrid,
  Treemap,
  LineChart,
  Line,
  AreaChart,
  Area,
  Cell,
  XAxis,
  YAxis,
  Tooltip
} from "recharts";

import numeral from "numeral";

import styles from "./style.module.css";

import { CHART_TYPES } from "containers/Report/ReportReducer";

import { graphSort } from "containers/Gallery/GalleryReducer";

const CustomToolTip = (props) => {
  const { active, payload, unit, isCurrencyUnit } = props;
  if (!active || !payload) {
    return null;
  }

  const dataKey = payload[0] && payload[0].dataKey;
  const retPayload = payload[0] && payload[0].payload;

  if (!retPayload) {
    return null;
  }

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px", color: "#333" }}>
      <div>
        <strong>{retPayload.label}</strong>
      </div>
      <div>
        {isCurrencyUnit ? <span>{unit}</span> : <span />}
        {numeral(retPayload[dataKey]).format("0,0.[0]")}
        {!isCurrencyUnit ? <span style={{ marginLeft: "5px" }}>{unit}</span> : <span />}
      </div>
    </div>
  );
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default class DistributionChart extends PureComponent {
  render() {
    const { chart, field, isRight, height, width, showTitle, showAxis } = this.props;

    if (field === undefined) {
      return <div />;
    }

    if (field.type === "number" && false) {
      return (
        <div className={isRight ? styles.numDistRight : styles.numDist}>
          {field.min + " to " + field.max}
          {isRight !== true && this.renderCorrelation()}
        </div>
      );
    }

    const rawBars =
      field && field.data && field.data.length
        ? [
            ...field.data
              .sort((a, b) =>
                a.max && a.max < b.max
                  ? -1
                  : a.max && a.max > b.max
                    ? 1
                    : a.min && a.min < b.min
                      ? 1
                      : a.min && a.min > b.min
                        ? -1
                        : 0
              )
              .map((d) => ({
                label: d.value,
                name: d.value,
                count: d.count,
                max: d.max,
                min: d.min
              }))
          ]
        : [];

    const bars = graphSort(rawBars, "label");

    return (
      <div className={styles.distributionChart} style={isRight ? {} : { border: "none" }}>
        {showTitle && <div className={styles.dTitle}>{field.label + " Distribution"}</div>}
        {isRight && <div className={styles.dTitle}>{"Distribution & Statistics"}</div>}
        <ResponsiveContainer>
          {chart && chart.chartType === CHART_TYPES.TREEMAP ? (
            <Treemap
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }}
              data={bars.filter((b) => b.count !== undefined)}
              dataKey="count"
              fill={"#63C7FF"}
              animationDuration={1}
            >
              <Tooltip />
            </Treemap>
          ) : chart && chart.chartType === CHART_TYPES.LINE ? (
            <LineChart
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }}
              data={bars.filter((b) => b.count !== undefined)}
              dataKey="count"
              fill={"#63C7FF"}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <YAxis
                dataKey={"count"}
                type={"number"}
                domain={[0, "dataMax"]}
                hide={isRight || showAxis ? false : true}
              />
              <XAxis dataKey={"label"} type={"category"} hide={isRight || showAxis ? false : true} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#63C7FF" />
            </LineChart>
          ) : chart && chart.chartType === CHART_TYPES.AREA ? (
            <AreaChart
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }}
              data={bars.filter((b) => b.count !== undefined)}
              dataKey="count"
              fill={"#63C7FF"}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <YAxis
                dataKey={"count"}
                type={"number"}
                domain={[0, "dataMax"]}
                hide={isRight || showAxis ? false : true}
              />
              <XAxis dataKey={"label"} type={"category"} hide={isRight || showAxis ? false : true} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#63C7FF" />
            </AreaChart>
          ) : chart && chart.chartType === CHART_TYPES.PIE ? (
            <PieChart
              margin={{
                top: 10,
                right: bars.reduce((acc, b) => Math.max(b.name.length, acc), 0) * 6,
                bottom: isRight ? 15 : 10,
                left: bars.reduce((acc, b) => Math.max(b.name.length, acc), 0) * 6
              }}
            >
              <Tooltip />
              <Pie
                data={bars.filter((b) => b.count !== undefined)}
                dataKey={"count"}
                fill={"#63C7FF"}
                label={(e) => e.name}
              />
            </PieChart>
          ) : (
            <BarChart
              data={bars.filter((b) => b.count !== undefined)}
              layout={"horizontal"}
              margin={{
                top: 10,
                right: 10,
                bottom: isRight ? 15 : 10,
                left: 10
              }}
            >
              <YAxis
                dataKey={"count"}
                type={"number"}
                domain={[0, "dataMax"]}
                hide={isRight || showAxis ? false : true}
              />
              <XAxis dataKey={"label"} type={"category"} hide={isRight || showAxis ? false : true} />
              <Tooltip />
              <Bar dataKey={"count"}>
                {bars.map((p, index) => (
                  <Cell key={index} fill={"#63C7FF"} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    );
  }
}
